import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import FormNoteAttachmentsView from "@cayuse/shared-react.components.form-note-attachments-view";
import { isNumber } from "@cayuse/shared-react.utils.type-checks";

import {
    spUrl,
    notifyError,
    downloadMultipartFile,
    convertToPlainText,
    mapAdminNotesResponse
} from "utils";
import { downloadFile, save } from "utils/proxy";
import { validate as uuidValidate } from "uuid";
import {
    ROLE_SP_SYSTEM_ADMINISTRATOR,
    ADMIN_ONLY_NOTES_MAX_LENGTH
} from "globals/constants";
import { useHasRoles, useDictionary } from "hooks";
import { awardSelectors, shellSelectors, tenantSelectors } from "store/selectors";
import { api } from "services";
import { attachments } from "services/api/attachments";

const AdminNoteUrls = {
    downloadAttachment: (awardId, attachmentId) =>
        spUrl(`api/notes/awards/${awardId}/attachments/${attachmentId}/content`),
    uploadAttachment: (awardId) => spUrl(`api/notes/awards/${awardId}/attachments`),
};

// todo: refactor and create reusabel component for all types (proposal, project, etc.)
const AwardAdminOnlyView = ({ setIsUploading }) => {
    const { awardId } = useParams();
    const { fullName, personId } = useSelector(shellSelectors.user);
    const { projectAttachmentTypes } = useSelector(tenantSelectors.tenant);

    const {
        [ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
    } = useHasRoles([ ROLE_SP_SYSTEM_ADMINISTRATOR ]);

    const dictionary = {
        ...useDictionary("common.notes"),
        ...useDictionary("pagingControls"),
        ...useDictionary("proposals.attachments"),
    };

    const [ userNames, setUserNames ] = useState([]);

    const canEditNotes = (userId) => isSystemAdmin || userId === personId;

    const deleteItem = async (noteId) => {
        return api.awards.deleteNote({ noteId, awardId });
    };
    const deleteAttachment = async (attachmentId) => {
        return api.awards.deleteAttachment({ attachmentId, awardId });
    };
    const fetchItems = async (pageNumber, itemsPerPage) => {
        const response = await api.awards.getNotes(
            { awardId },
            {
                page: isNumber(pageNumber) ? pageNumber : undefined,
                size: isNumber(itemsPerPage) ? itemsPerPage : undefined,
            },
        );

        return mapAdminNotesResponse({
            response,
            userNames,
            setUserNames,
            dictionaryUnknownUser: dictionary.unknownUser,
        });
    };
    const validateNoteLength = message => {
        const decodedMessage = decodeURI(message);
        const plainTextMessage = convertToPlainText(decodedMessage);
        if (plainTextMessage.length > ADMIN_ONLY_NOTES_MAX_LENGTH) {
            notifyError(dictionary.msgTooLong ?? "Note is too long.");
            return false;
        }

        return true;
    }
    const saveItem = async (message) => {
        if (validateNoteLength(message)) {
            return api.awards.saveNote({ awardId }, { message: message });
        }
    };
    const updateItem = async (noteId, payload) => {
        if (validateNoteLength(payload.message)) {
            return api.awards.updateNote(
                { noteId, awardId },
                {
                    ...payload,
                    createdByFullName: undefined,
                    updatedByFullName: undefined,
                },
            );
        }
    };

    const uploadAttachment = async (payload) => {
        if(payload.noteId && !uuidValidate(payload.noteId)) {
            delete payload.noteId;
        }
        const url = AdminNoteUrls.uploadAttachment(awardId);
        const file = payload.attachment;

        const fileMetadata = await attachments.upload(file);

        let postUrl = `${url}?`;
        for(const property of ["attachmentType", "noteId"]) {
            if (property in payload) {
                const queryParam = `${property}=${encodeURIComponent(payload[property])}&`;
                postUrl = postUrl + queryParam
            }
        }

        if (!fileMetadata) {
            const error = new Error("Failed to upload file. Please try again.");
            notifyError(error.message, error);

            return;
        }

        const response = await save(postUrl, fileMetadata);
        return { ...response, uploadedBy: fullName };
    };

    const downloadAttachment = async (attachmentId, fileName, attachment) => {
        const filePath = attachment.filePath
        if (filePath?.startsWith("platform://")){
			return downloadMultipartFile({
				id: filePath.slice(11),
				fileName
			});
		}
        else {
            const url = AdminNoteUrls.downloadAttachment(awardId, attachmentId);
            return downloadFile(url, fileName);
        }
    };

    return (
        <FormNoteAttachmentsView
          attachmentTypes={projectAttachmentTypes}
          canEditNotes={canEditNotes}
          deleteAttachment={deleteAttachment}
          deleteItem={deleteItem}
          dictionary={dictionary}
          downloadFile={downloadAttachment}
          fetchItems={fetchItems}
          fullName={fullName}
          saveItem={saveItem}
          updateItem={updateItem}
          uploadAttachment={uploadAttachment}
          uploadingStateChanged={state => setIsUploading(state.isUploading)}
          //only for Netarts release
          hideSearchBox={true}
        />
    );
};

export default AwardAdminOnlyView;
